/**
 * InvoiceSummaryCard
 *
 * Displays invoice totals and status within the work order detail view.
 * Billing actions live on the Payments card (#1961).
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Stack,
  type CardProps,
} from '@mui/material';
import type { WorkOrderInvoice, InvoiceStatus } from '@/hooks/useInvoices';
import { PaymentHistorySection } from './PaymentHistorySection';
import { formatDate } from '@/utils/locale';


const STATUS_COLORS: Record<InvoiceStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'error',
  void: 'default',
  refunded: 'default',
  credited: 'default',
};

interface InvoiceSummaryCardProps {
  invoice: WorkOrderInvoice;
  cardVariant?: CardProps['variant'];
}

export function InvoiceSummaryCard({ invoice, cardVariant }: InvoiceSummaryCardProps) {
  const { t } = useTranslation('invoices');
  const { t: tw } = useTranslation('workorders');

  const remainingCents = invoice.totalCents - invoice.paidAmountCents;
  const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' && invoice.status !== 'void';

  return (
    <Card variant={cardVariant} sx={{ mb: 3 }} data-testid="invoice-summary-card">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{t('detail.invoiceNumber')}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{invoice.invoiceNumber}</Typography>
            <Chip
              size="small"
              label={t(`status.${invoice.status as InvoiceStatus}`)}
              color={STATUS_COLORS[invoice.status as InvoiceStatus] ?? 'default'}
            />
          </Stack>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">{t('detail.subtotal')}</Typography>
          <Typography variant="body2">{formatPrice(invoice.subtotalCents)}</Typography>
        </Box>
        {(invoice.surchargeCents ?? 0) > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{tw('detail.smallSuppliesSurcharge')}</Typography>
            <Typography variant="body2">{formatPrice(invoice.surchargeCents ?? 0)}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">{t('detail.tax', { percent: invoice.taxPercent })}</Typography>
          <Typography variant="body2">{formatPrice(invoice.taxCents)}</Typography>
        </Box>
        {invoice.discountCents > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{t('detail.discount')}</Typography>
            <Typography variant="body2" color="error.main">-{formatPrice(invoice.discountCents)}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>{t('detail.total')}</Typography>
          <Typography variant="subtitle2" fontWeight={600}>{formatPrice(invoice.totalCents)}</Typography>
        </Box>
        {invoice.paidAmountCents > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{t('detail.paidAmount')}</Typography>
            <Typography variant="body2" color="success.main">{formatPrice(invoice.paidAmountCents)}</Typography>
          </Box>
        )}
        {remainingCents > 0 && invoice.status !== 'void' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>{t('detail.remaining')}</Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">{formatPrice(remainingCents)}</Typography>
          </Box>
        )}

        {invoice.status !== 'void' && (invoice.payments && invoice.payments.length > 0) && (
          <PaymentHistorySection
            payments={invoice.payments}
            remainingCents={remainingCents}
          />
        )}

        {invoice.dueDate && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('detail.dueDate')}</Typography>
            <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'} fontWeight={isOverdue ? 600 : 400}>
              {formatDate(invoice.dueDate)}
              {isOverdue && ` — ${t('detail.overdue')}`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
